import { useEffect, useMemo, useRef, useState } from "react";
import { useThreadMessages } from "@/hooks/agent/useThreadMessages";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";
import { useAgentStore } from "@/stores/agent/useAgentStore";
import MessageItemUI from "./MessageItemUI";
import { MessageSkeleton } from "./MessageSkeleton";

interface MessageListUIProps {
  threadId: string;
  className?: string;
  scrollRef?: React.RefObject<HTMLDivElement>;
}

const MessageListUI: React.FC<MessageListUIProps> = ({
  threadId,
  className,
  scrollRef,
}) => {
  const setSelectedToolCallMessage = useAgentStore((state) => state.setSelectedToolCallMessage);
  const setIsOpenToolPanel = useAgentStore((state) => state.setIsOpenToolPanel);
  const latestStatusMessage = useAgentStore((state) => state.latestStatusMessage);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [prevMessageLength, setPrevMessageLength] = useState(0);

  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    observerRef,
  } = useThreadMessages(threadId, scrollRef);

  const lastMessage = messages.length > 0 ? messages[0] : null;

  const isAgentRunning = useMemo(() => {
    const latestUserOrEndMessage = messages.find((msg) => ["assistant_response_end", "user"].includes(msg.role));
    return latestUserOrEndMessage?.role !== "assistant_response_end";
  }, [messages]);

  useEffect(() => {
    setSelectedToolCallMessage(null);
    setIsOpenToolPanel(false);
  }, [threadId, setSelectedToolCallMessage, setIsOpenToolPanel]);

  const handleToolClick = (toolMessage: AgentMessage) => {
    setSelectedToolCallMessage(toolMessage);
    setIsOpenToolPanel(true);
  };

  useEffect(() => {
    // 新しいメッセージが追加されたときにスクロール
    const isNewUserMessage = messages.length > prevMessageLength && lastMessage?.role === "user";
    if (isNewUserMessage) {
      requestAnimationFrame(() => {
        messagesContainerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
      });
    }

    // 初回ロード時にスクロール
    const isFirstLoad = prevMessageLength === 0 && messages.length > 0;
    if (isFirstLoad) {
      requestAnimationFrame(() => {
        scrollRef?.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      });
    }

    setPrevMessageLength(messages.length);
  }, [messages.length, lastMessage, prevMessageLength, scrollRef]);

  const reversedMessages = useMemo(() => messages.toReversed(), [messages]);

  return (
    <div className={cn(className)}>
      {/* 上スクロール検知用のトリガー要素 */}
      {hasMore && !isLoadingMore && messages.length > 0 && (
        <div ref={observerRef} className="h-4 flex items-center justify-center">
          <div className="" />
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading
        ? <MessageSkeleton count={3} className="mt-5" />
        : (
          <div className="space-y-4" ref={messagesContainerRef}>
            {reversedMessages.map((message) => (
              <MessageItemUI
                key={message.id}
                message={message}
                className="mt-5 scroll-mt-[-20px]"
                onToolClick={handleToolClick}
              />
            ))}

            {latestStatusMessage && (
              <MessageItemUI
                key="latest-status-message"
                message={latestStatusMessage}
                className="items-start"
              />
            )}
          </div>
        )}

      <div ref={messagesEndRef} className={cn(isAgentRunning ? "h-[calc(-302px+100vh)]" : "h-10")} />
    </div>
  );
};

export default MessageListUI;
